import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound, Loader2, Save, UserPlus, ShieldCheck, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPasswordsPage() {
  const { token } = useAuth();
  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const [accounts, setAccounts] = useState(null);
  const [pw, setPw] = useState({});     // { email: 'newpassword' }
  const [busy, setBusy] = useState(null);
  const [newAcct, setNewAcct] = useState({ email: '', password: '', company_name: 'SelleSend', make_admin: false });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const r = await fetch(`${API}/admin/accounts`, { headers: authHeaders });
    if (!r.ok) { toast.error('Admin only'); setAccounts([]); return; }
    const d = await r.json();
    setAccounts(d.accounts);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const reset = async (email) => {
    const newp = (pw[email] || '').trim();
    if (newp.length < 3) { toast.error('Enter a new password (min 3 chars)'); return; }
    setBusy(email);
    try {
      const r = await fetch(`${API}/admin/accounts/reset`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ email, new_password: newp }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Reset failed');
      toast.success(`Password reset for ${email}`);
      setPw((p) => ({ ...p, [email]: '' }));
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const toggleAdmin = async (email, current) => {
    setBusy(`admin:${email}`);
    try {
      const r = await fetch(`${API}/admin/accounts/grant-admin`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ email, is_admin: !current }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      toast.success(`${email} is now ${j.is_admin ? 'Admin' : 'Merchant'}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const create = async () => {
    if (!newAcct.email || !newAcct.password) { toast.error('Email and password required'); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/admin/accounts/create`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify(newAcct),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Create failed');
      toast.success(`Created ${j.email}`);
      setNewAcct({ email: '', password: '', company_name: 'SelleSend', make_admin: false });
      load();
    } catch (e) { toast.error(e.message); }
    finally { setCreating(false); }
  };

  if (!accounts) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="admin-passwords-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <KeyRound className="w-6 h-6 text-indigo-500" /> Admin · Account Passwords
        </h1>
        <p className="text-slate-500 mt-1">Reset any password instantly. No code changes, no republish.</p>
      </div>

      {/* Create new account */}
      <Card data-testid="create-account-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><UserPlus className="w-4 h-4" /> Create new account</CardTitle>
          <CardDescription>Instantly provision a new user with a chosen password.</CardDescription>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label>Email</Label>
            <Input value={newAcct.email} onChange={(e) => setNewAcct({ ...newAcct, email: e.target.value })}
                   placeholder="new@sellesend.com" data-testid="new-acct-email" />
          </div>
          <div>
            <Label>Password</Label>
            <Input value={newAcct.password} onChange={(e) => setNewAcct({ ...newAcct, password: e.target.value })}
                   placeholder="password" data-testid="new-acct-password" />
          </div>
          <div>
            <Label>Company</Label>
            <Input value={newAcct.company_name} onChange={(e) => setNewAcct({ ...newAcct, company_name: e.target.value })} />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={newAcct.make_admin}
                     onChange={(e) => setNewAcct({ ...newAcct, make_admin: e.target.checked })}
                     data-testid="new-acct-admin" />
              Make admin
            </label>
          </div>
          <div className="flex items-end">
            <Button onClick={create} disabled={creating} className="w-full" data-testid="new-acct-create">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
              Create
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Accounts list */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">All accounts ({accounts.length})</CardTitle>
          <CardDescription>Type a new password and click Reset. Toggle admin role in one click.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {accounts.map((a) => {
            const isAdmin = a.is_admin || a.role === 'platform_admin';
            const rowKey = a.email;
            const adminBusy = busy === `admin:${a.email}`;
            const resetBusy = busy === a.email;
            return (
              <div key={a.id} className="border rounded-md p-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end"
                   data-testid={`account-row-${a.id.slice(0, 8)}`}>
                <div className="lg:col-span-2">
                  <div className="text-sm font-medium truncate">{a.email}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    {isAdmin ? (
                      <span className="text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded text-[10px]">Admin</span>
                    ) : (
                      <span className="text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">Merchant</span>
                    )}
                    {a.company_name && <span>· {a.company_name}</span>}
                    {a.seed_reset_at && <span>· last reset {new Date(a.seed_reset_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div>
                  <Label>New password</Label>
                  <Input value={pw[rowKey] || ''} onChange={(e) => setPw({ ...pw, [rowKey]: e.target.value })}
                         placeholder="e.g. admin1" data-testid={`newpw-${a.id.slice(0, 8)}`} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => reset(a.email)} disabled={resetBusy} className="flex-1"
                          data-testid={`reset-${a.id.slice(0, 8)}`}>
                    {resetBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Reset
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleAdmin(a.email, isAdmin)} disabled={adminBusy}
                          title={isAdmin ? 'Revoke admin' : 'Grant admin'}
                          data-testid={`admin-${a.id.slice(0, 8)}`}>
                    {adminBusy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                      isAdmin ? <Shield className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
